import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Calendar, ArrowLeft, Share2, CheckCircle, Phone, ChevronRight } from 'lucide-react';
import { fetchArticleBySlug, fetchArticles, getPlaceholderImage } from '../utils/articleApi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      setError('');
      
      try {
        const articleData = await fetchArticleBySlug(slug);
        setArticle(articleData);
        
        const relatedResult = await fetchArticles({
          page: 1,
          limit: 3,
          category: articleData.category
        });
        
        const filtered = relatedResult.articles
          .filter(post => post.slug !== slug)
          .slice(0, 3);
        
        setRelatedArticles(filtered);
      } catch (err) {
        setError(err.message || 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadArticle();
    }
  }, [slug]);

  if (loading) {
    return (
      <div style={styles.page}>
        <Navbar />
        <div style={styles.loadingContainer}>
          <div style={styles.skeletonHero}>
            <div style={{ width: '100px', height: '20px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '20px' }}></div>
            <div style={{ width: '60px', height: '20px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '20px' }}></div>
            <div style={{ width: '100%', height: '60px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '32px' }}></div>
            <div style={{ width: '200px', height: '20px', background: '#E0E4E8', borderRadius: '4px' }}></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={styles.page}>
        <Navbar />
        <div style={styles.notFound}>
          <h1>Article Not Found</h1>
          <p style={{ color: '#8F9BA8', marginBottom: '24px' }}>{error || 'The article you are looking for does not exist.'}</p>
          <button onClick={() => navigate('/articles')} style={styles.backButton}>
            Back to Articles
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Navbar />
      
      <section style={styles.heroSection}>
        <div style={styles.heroContainer}>
          <button onClick={() => navigate('/articles')} style={styles.backBtn}>
            <ArrowLeft size={18} style={{ marginRight: '8px' }} />
            Back to Resources
          </button>
          
          <div style={styles.categoryBadge}>{article.category}</div>
          
          <h1 style={styles.heroTitle} className="hero-title">{article.title}</h1>
          
          <div style={styles.heroMeta}>
            <div style={styles.authorSection}>
              <div style={styles.authorAvatar}>
                {article.author.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <div style={styles.authorName}>{article.author}</div>
                <div style={styles.authorTitle}>{article.authorTitle}</div>
              </div>
            </div>
            
            <div style={styles.metaDivider}></div>
            
            <div style={styles.metaItems}>
              <span style={styles.metaItem}>
                <Calendar size={16} />
                {new Date(article.date).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
              <span style={styles.metaItem}>
                <Clock size={16} />
                {article.readTime}
              </span>
            </div>
          </div>

          <div style={styles.reviewedBadge}>
            <CheckCircle size={18} color="#28C08A" />
            <span>Medically Reviewed by Healthcare Professionals</span>
          </div>
        </div>
      </section>

      <section style={styles.imageSection}>
        <div style={styles.imageContainer}>
          <img 
            src={article.image || getPlaceholderImage(1000, 500, 'Featured Image')} 
            alt={article.title} 
            style={styles.featuredImage}
            onError={(e) => {
              e.target.src = getPlaceholderImage(1000, 500, 'Featured Image');
            }}
          />
        </div>
      </section>

      <section style={styles.contentSection}>
        <div style={styles.contentWrapper} className="article-wrapper">
          <div style={styles.mainColumn} className="article-main-column">
            <article style={styles.articleContent}>
              <div 
                dangerouslySetInnerHTML={{ __html: article.content }}
                className="article-body"
              />

              <blockquote style={styles.pullQuote}>
                <p style={styles.pullQuoteText}>
                  "Early detection and proper medical care can significantly improve health outcomes. 
                  Don't hesitate to consult with healthcare professionals."
                </p>
              </blockquote>
            </article>

            <div style={styles.bottomCTA} className="bottom-cta">
              <div style={styles.ctaIcon}>
                <Phone size={32} color="#286CFF" />
              </div>
              <h3 style={styles.ctaTitle}>Need Medical Advice?</h3>
              <p style={styles.ctaText}>
                Consult with our experienced healthcare professionals to get personalized advice 
                related to this topic.
              </p>
              <button 
                style={styles.ctaButton}
                onClick={() => navigate('/specializations')}
              >
                Consult Now
                <ChevronRight size={20} style={{ marginLeft: '8px' }} />
              </button>
            </div>

            <div style={styles.disclaimer}>
              <h4 style={styles.disclaimerTitle}>Medical Disclaimer</h4>
              <p style={styles.disclaimerText}>
                The information provided in this article is for educational purposes only and 
                should not be considered as medical advice. Always consult with a qualified 
                healthcare professional before making any healthcare decisions or for guidance 
                about a specific medical condition.
              </p>
            </div>

            {relatedArticles.length > 0 && (
              <div style={styles.relatedSection}>
                <h3 style={styles.relatedTitle}>You Might Also Like</h3>
                <div style={styles.relatedGrid} className="related-grid">
                  {relatedArticles.map(post => (
                    <div
                      key={post.id}
                      style={styles.relatedCard}
                      onClick={() => navigate(`/article/${post.slug}`)}
                    >
                      <img 
                        src={post.image || getPlaceholderImage(400, 180)} 
                        alt={post.title} 
                        style={styles.relatedImage}
                        onError={(e) => {
                          e.target.src = getPlaceholderImage(400, 180);
                        }}
                      />
                      <div style={styles.relatedContent}>
                        <div style={styles.relatedCategory}>{post.category}</div>
                        <h4 style={styles.relatedCardTitle}>{post.title}</h4>
                        <p style={styles.relatedExcerpt}>{post.excerpt}</p>
                        <div style={styles.relatedMeta}>
                          <Clock size={14} />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside style={styles.sidebar} className="article-sidebar">
            <div style={styles.sidebarSticky}>
              <div style={styles.appointmentCard}>
                <div style={styles.appointmentIcon}>
                  <Phone size={24} color="white" />
                </div>
                <h4 style={styles.appointmentTitle}>Book an Appointment</h4>
                <p style={styles.appointmentText}>
                  Get expert medical advice from qualified healthcare professionals.
                </p>
                <button 
                  style={styles.appointmentButton}
                  onClick={() => navigate('/specializations')}
                >
                  Schedule Now
                </button>
              </div>

              <button style={styles.sidebarShareBtn} title="Share article">
                <Share2 size={20} />
                <span>Share Article</span>
              </button>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FAFBFC'
  },
  loadingContainer: {
    padding: '120px 0 60px',
    marginTop: '70px'
  },
  skeletonHero: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 24px'
  },
  heroSection: {
    background: 'white',
    padding: '120px 0 60px',
    borderBottom: '1px solid #E8ECF0'
  },
  heroContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 24px'
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#286CFF',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '0',
    transition: 'opacity 0.2s ease'
  },
  categoryBadge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #28C08A 0%, #23a575 100%)',
    color: 'white',
    padding: '8px 18px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '20px',
    letterSpacing: '0.5px'
  },
  heroTitle: {
    fontSize: '52px',
    fontWeight: '800',
    lineHeight: '1.15',
    color: '#1a202c',
    marginBottom: '32px',
    letterSpacing: '-0.5px'
  },
  heroMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    marginBottom: '20px'
  },
  authorSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  authorAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #286CFF 0%, #1a52c9 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '700',
    border: '2px solid white',
    boxShadow: '0 2px 8px rgba(40, 108, 255, 0.2)'
  },
  authorName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1a202c',
    marginBottom: '2px'
  },
  authorTitle: {
    fontSize: '13px',
    color: '#718096'
  },
  metaDivider: {
    width: '1px',
    height: '40px',
    background: '#E2E8F0'
  },
  metaItems: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#718096',
    fontWeight: '500'
  },
  reviewedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'rgba(40, 192, 138, 0.08)',
    border: '1px solid #28C08A',
    borderRadius: '24px',
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#28C08A'
  },
  imageSection: {
    background: 'white',
    padding: '0 0 60px'
  },
  imageContainer: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '0 24px'
  },
  featuredImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)'
  },
  contentSection: {
    padding: '60px 0 80px',
    background: '#FAFBFC'
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'grid',
    gridTemplateColumns: '1fr 340px',
    gap: '60px',
    alignItems: 'start'
  },
  mainColumn: {
    background: 'white',
    borderRadius: '12px',
    padding: '48px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
  },
  articleContent: {
    marginBottom: '60px'
  },
  pullQuote: {
    background: 'rgba(40, 192, 138, 0.06)',
    borderLeft: '4px solid #28C08A',
    borderRadius: '0 8px 8px 0',
    padding: '24px 32px',
    margin: '32px 0',
    fontStyle: 'italic'
  },
  pullQuoteText: {
    fontSize: '20px',
    lineHeight: '1.6',
    color: '#2d3748',
    margin: 0,
    fontWeight: '500'
  },
  bottomCTA: {
    background: 'linear-gradient(135deg, #EBF4FF 0%, #E3F0FF 100%)',
    border: '2px solid #286CFF',
    borderRadius: '16px',
    padding: '40px',
    textAlign: 'center',
    marginBottom: '48px'
  },
  ctaIcon: {
    width: '64px',
    height: '64px',
    background: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 4px 12px rgba(40, 108, 255, 0.15)'
  },
  ctaTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px'
  },
  ctaText: {
    fontSize: '16px',
    color: '#4a5568',
    lineHeight: '1.6',
    marginBottom: '24px',
    maxWidth: '600px',
    margin: '0 auto 24px'
  },
  ctaButton: {
    background: 'linear-gradient(135deg, #286CFF 0%, #1a52c9 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 4px 16px rgba(40, 108, 255, 0.3)',
    transition: 'all 0.2s ease'
  },
  disclaimer: {
    background: 'rgba(250, 229, 145, 0.15)',
    border: '2px solid #FFB800',
    borderRadius: '12px',
    padding: '28px',
    marginBottom: '48px'
  },
  disclaimerTitle: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  disclaimerText: {
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#4a5568',
    margin: 0
  },
  relatedSection: {
    borderTop: '2px solid #E2E8F0',
    paddingTop: '48px'
  },
  relatedTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    marginBottom: '32px'
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px'
  },
  relatedCard: {
    background: '#FAFBFC',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  relatedImage: {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
    borderRadius: '8px'
  },
  relatedContent: {
    padding: '20px'
  },
  relatedCategory: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#28C08A',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px'
  },
  relatedCardTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#1a202c',
    marginBottom: '10px',
    lineHeight: '1.4'
  },
  relatedExcerpt: {
    fontSize: '14px',
    color: '#718096',
    lineHeight: '1.6',
    marginBottom: '12px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  relatedMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#a0aec0',
    fontWeight: '500'
  },
  sidebar: {
    position: 'relative'
  },
  sidebarSticky: {
    position: 'sticky',
    top: '100px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  appointmentCard: {
    background: 'linear-gradient(135deg, #286CFF 0%, #1a52c9 100%)',
    borderRadius: '12px',
    padding: '28px',
    textAlign: 'center',
    color: 'white'
  },
  appointmentIcon: {
    width: '56px',
    height: '56px',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  },
  appointmentTitle: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '10px'
  },
  appointmentText: {
    fontSize: '14px',
    lineHeight: '1.5',
    marginBottom: '20px',
    opacity: 0.95
  },
  appointmentButton: {
    background: 'white',
    color: '#286CFF',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s ease'
  },
  sidebarShareBtn: {
    background: 'white',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  notFound: {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px'
  },
  backButton: {
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '28px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '24px'
  }
};

const mediaQueries = document.createElement('style');
mediaQueries.textContent = `
  @media (max-width: 1024px) {
    .article-wrapper {
      grid-template-columns: 1fr !important;
      gap: 0 !important;
    }
    
    .article-sidebar {
      display: none !important;
    }
    
    .article-main-column {
      padding: 32px 24px !important;
    }
  }
  
  @media (max-width: 768px) {
    .hero-title {
      font-size: 36px !important;
    }
    
    .article-main-column {
      padding: 24px 20px !important;
    }
    
    .related-grid {
      grid-template-columns: 1fr !important;
    }
    
    .bottom-cta {
      padding: 32px 24px !important;
    }
  }
`;
document.head.appendChild(mediaQueries);

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .article-body {
    font-size: 18px;
    line-height: 1.6;
    color: #333333;
    max-width: 750px;
    margin: 0 auto;
  }
  
  .article-body h2 {
    font-size: 32px;
    font-weight: 700;
    color: #28C08A;
    margin: 40px 0 20px;
    line-height: 1.3;
    letter-spacing: -0.5px;
  }
  
  .article-body h3 {
    font-size: 26px;
    font-weight: 600;
    color: #28C08A;
    margin: 32px 0 16px;
    line-height: 1.3;
  }
  
  .article-body h4 {
    font-size: 22px;
    font-weight: 600;
    color: #2d3748;
    margin: 28px 0 14px;
    line-height: 1.3;
  }
  
  .article-body p {
    margin-bottom: 24px;
    line-height: 1.6;
    color: #333333;
  }
  
  .article-body ul, .article-body ol {
    margin: 24px 0;
    padding-left: 28px;
  }
  
  .article-body li {
    margin-bottom: 14px;
    line-height: 1.6;
    color: #333333;
  }
  
  .article-body strong {
    font-weight: 600;
    color: #1a202c;
  }
  
  .article-body a {
    color: #286CFF;
    text-decoration: underline;
    font-weight: 500;
  }
  
  .article-body img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    margin: 32px 0;
  }
  
  .article-body blockquote {
    background: rgba(40, 192, 138, 0.06);
    border-left: 4px solid #28C08A;
    border-radius: 0 8px 8px 0;
    padding: 20px 28px;
    margin: 28px 0;
    font-style: italic;
    color: #2d3748;
  }
  
  .article-body code {
    background: #F7FAFC;
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 16px;
    color: #d63384;
  }
  
  .article-body pre {
    background: #2d3748;
    color: #fff;
    padding: 20px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 28px 0;
  }
  
  .article-body pre code {
    background: none;
    color: #fff;
    padding: 0;
  }
  
  @media (max-width: 1024px) {
    .article-body {
      font-size: 17px;
      max-width: 100%;
    }
    
    .article-body h2 {
      font-size: 28px;
    }
    
    .article-body h3 {
      font-size: 24px;
    }
  }
  
  @media (max-width: 768px) {
    .article-body {
      font-size: 16px;
    }
    
    .article-body h2 {
      font-size: 24px;
      margin: 32px 0 16px;
    }
    
    .article-body h3 {
      font-size: 20px;
      margin: 24px 0 12px;
    }
  }
`;
document.head.appendChild(styleSheet);

export default ArticleDetail;
